import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import './TeamPage.css';

export default function EventPage() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, 'events', id));
        if (!snap.exists() || snap.data().approved !== true) {
          setNotFound(true);
        } else {
          setEvent({ id: snap.id, ...snap.data() });
        }
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) return (
    <div className="tpage__loading">
      <div className="tpage__spinner" />
    </div>
  );

  if (notFound) return (
    <div className="tpage__notfound">
      <div className="tpage__notfound-icon">🔍</div>
      <h2>Event Not Found</h2>
      <p>This event may not be approved yet or the link may be incorrect.</p>
      <a href="/" className="btn btn-primary">Go Home</a>
    </div>
  );

  const sportEmojis = {
    Baseball: '⚾', Basketball: '🏀', 'Field Hockey': '🏑', Football: '🏈',
    'Ice Hockey': '🏒', Lacrosse: '🥍', Soccer: '⚽', Softball: '🥎', Volleyball: '🏐',
  };

  return (
    <div className="tpage">
      {/* Header */}
      <div className="tpage__hero tpage__hero--event">
        <div className="tpage__hero-bg" />
        <div className="tpage__hero-inner">
          {event.logoUrl
            ? <img src={event.logoUrl} alt={event.name} className="tpage__logo" />
            : <div className="tpage__logo-placeholder tpage__logo-placeholder--event">{event.name?.[0]}</div>
          }
          <div className="tpage__info">
            <div className="tpage__sport tpage__sport--event">
              {sportEmojis[event.sport] || '🏅'} {event.sport}
            </div>
            <h1 className="tpage__name">{event.name}</h1>
            <div className="tpage__location">
              📍 {event.city}, {event.state}
            </div>
            {event.startDate && (
              <div className="tpage__dates">
                📅 {formatDate(event.startDate)}
                {event.endDate && ` – ${formatDate(event.endDate)}`}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Social links */}
      {(event.website || event.facebook || event.instagram) && (
        <div className="tpage__social">
          {event.website && (
            <a href={event.website} target="_blank" rel="noopener noreferrer" className="tpage__social-btn">
              🌐 Website
            </a>
          )}
          {event.facebook && (
            <a href={event.facebook} target="_blank" rel="noopener noreferrer" className="tpage__social-btn tpage__social-btn--fb">
              📘 Facebook
            </a>
          )}
          {event.instagram && (
            <a href={event.instagram} target="_blank" rel="noopener noreferrer" className="tpage__social-btn tpage__social-btn--ig">
              📸 Instagram
            </a>
          )}
        </div>
      )}

      {/* App download CTA */}
      <div className="tpage__cta">
        <div className="tpage__cta-inner">
          <div className="tpage__cta-patch">🎫</div>
          <h2 className="tpage__cta-title">Collect This Event Patch!</h2>
          <p className="tpage__cta-desc">
            Download The Patch Guys app to scan this event's QR code and add it to your collection.
          </p>
          <div className="tpage__cta-buttons">
            <a href="https://apps.apple.com/app/id6760299209" target="_blank" rel="noopener noreferrer" className="tpage__store-btn tpage__store-btn--apple">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              Download on App Store
            </a>
            <a href="https://play.google.com" target="_blank" rel="noopener noreferrer" className="tpage__store-btn tpage__store-btn--google">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3.18 23.76c.3.17.64.24.99.2l12.47-7.2-2.75-2.76-10.71 9.76zM20.7 10.06L17.6 8.28 14.54 11.3l3.1 3.1 3.07-1.79a1.79 1.79 0 0 0 0-2.55zM2.08.47A1.79 1.79 0 0 0 1.5 1.83v20.34c0 .54.22 1.02.58 1.36l.07.07L13.72 12 2.15.4l-.07.07zM3.18.24l10.71 9.76 2.75-2.76L4.17.04a1.3 1.3 0 0 0-.99.2z"/>
              </svg>
              Get it on Google Play
            </a>
          </div>
          <p className="tpage__cta-hint">Already have the app? Open it and scan the QR code on the patch!</p>
        </div>
      </div>
    </div>
  );
}